// translations/pages/kyveil/uk.js

export const kyveilPageUK = {
  "seo": {
    "title": "Анонімізація PII з Kyveil | pAIoneer Labs GmbH",
    "description": "Kyveil виявляє та анонімізує персональні дані локально, у ваших системах. Настільний застосунок або REST API для анонімізації PII відповідно до GDPR.",
    "keywords": "Kyveil, анонімізація PII, анонімізація персональних даних, захист даних, GDPR, Microsoft Presidio, spaCy, flair, локальна обробка даних",
    "ogTitle": "Kyveil: професійна анонімізація PII за допомогою ШІ та NLP",
    "ogDescription": "Автоматичне виявлення та анонімізація персональних даних у вигляді настільного застосунку або REST API у вашій власній інфраструктурі.",
    "twitterTitle": "Анонімізація PII з Kyveil",
    "twitterDescription": "Локальне виявлення та анонімізація PII для чутливих документів.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Програмне забезпечення для локального виявлення та анонімізації персональних даних за допомогою ШІ та NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Захист даних та анонімізація",
    "title": "Kyveil",
    "subtitle": "Локальна анонімізація перед ШІ, тестами та передаванням.",
    "description": "Kyveil — це локальний рівень анонімізації для документів і робочих процесів із ШІ. Програма виявляє персональні дані у файлах Word, Excel, PDF і текстових документах, замінює або маскує їх і показує попередній перегляд перед передаванням у ШІ-чати, тести, аналітику чи зовнішнім одержувачам. Kyveil розробляється та безперервно вдосконалюється на основі [IDD](idd) (Intent-Driven Development) й автоматизовано за допомогою [Veridion](veridion) нашими ШІ-співробітниками за участю людини в контурі.",
    "platforms": "Настільний застосунок, REST API та AnonChat",
    "proofPoints": [
      "Документи залишаються локально у ваших системах.",
      "Персональні дані замінюються або маскуються.",
      "Попередній перегляд показує анонімізований вміст перед передаванням або ШІ-чатом.",
      "Word, Excel, PDF і текст — основні сценарії роботи з документами."
    ]
  },
  "dataSovereignty": {
    "title": "Ваші дані. Ваші системи. Ваш контроль.",
    "subtitle": "Повний суверенітет даних завдяки локальній обробці.",
    "items": [
      {
        "title": "Хмара не потрібна",
        "description": "Уся обробка відбувається локально у вашій інфраструктурі. Ваші файли ніколи не залишають ваших систем. Жодної залежності від хмари, жодного передавання даних назовні."
      },
      {
        "title": "Периферійні обчислення",
        "description": "Настільний застосунок працює повністю автономно на робочих станціях. API розгортається на ваших власних серверах. Обидва варіанти обробляють усі дані локально в межах вашого периметра безпеки."
      },
      {
        "title": "Автономія даних",
        "description": "Повний контроль над вашими потоками даних. Жодної прив’язки до постачальника. Жодних регулярних витрат на хмару. Ваша чутлива інформація залишається виключно під вашим контролем."
      },
      {
        "title": "Стандартне обладнання",
        "description": "GPU не потрібен. Працює на звичайних офісних комп’ютерах і стандартних серверах. Інвестиції в спеціалізоване обладнання не потрібні."
      }
    ]
  },
  "features": {
    "title": "Ключові функції",
    "subtitle": "Усе необхідне для контрольованого виявлення та анонімізації PII в бізнес-процесах.",
    "items": [
      {
        "title": "Виявлення PII на основі ШІ",
        "description": "Автоматичне розпізнавання за допомогою Microsoft Presidio SDK, spaCy від Explosion AI та flair від Humboldt-Universität zu Berlin.",
        "tone": "primary"
      },
      {
        "title": "Режим заміни",
        "description": "Замінює виявлені PII універсальними заповнювачами на кшталт [NAME], [EMAIL] та [ADDRESS] для сценаріїв, пов’язаних із дотриманням нормативних вимог.",
        "tone": "info"
      },
      {
        "title": "Режим фіктивних даних",
        "description": "Замінює виявлені PII правдоподібними підставними значеннями для тестових середовищ і розробки.",
        "tone": "warning"
      },
      {
        "title": "Кілька форматів документів",
        "description": "Обробляє файли Microsoft Word, Excel, OpenDocument, PDF і звичайний текст.",
        "tone": "success"
      },
      {
        "title": "Багатомовна підтримка",
        "description": "Вбудована підтримка німецької, англійської, французької, італійської та іспанської мов з окремими NLP-моделями для кожної мови.",
        "tone": "secondary"
      },
      {
        "title": "Налаштовуване виявлення",
        "description": "Визначайте, які сутності PII слід виявляти: імена, адреси, адреси електронної пошти, номери телефонів, банківські рахунки, податкові номери тощо.",
        "tone": "primary"
      },
      {
        "title": "Розширюваність",
        "description": "Інтерфейси для власної логіки попередньої та подальшої обробки, щоб інтегрувати рішення у ваші робочі процеси.",
        "tone": "info"
      },
      {
        "title": "Оптимізовано для CPU",
        "description": "Ефективна архітектура моделей працює на стандартному офісному обладнанні й не потребує GPU.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Технологічний стек",
    "subtitle": "Фреймворки корпоративного рівня та перевірені NLP-дослідження.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Галузевий стандарт серед фреймворків для розпізнавання та анонімізації PII з вбудованими розпізнавачами сутностей.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Розроблено компанією Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Готова до промислового використання NLP-бібліотека з попередньо навченими статистичними моделями для розпізнавання іменованих сутностей.",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Розроблено в Humboldt-Universität zu Berlin",
        "description": "Сучасний NLP-фреймворк із контекстними рядковими ембедингами. Передові нейромережеві моделі для точнішого виявлення сутностей.",
        "tone": "secondary"
      }
    ],
    "integration": "Інтегрований ШІ-пайплайн",
    "integrationDesc": "Комбіноване розпізнавання: Presidio як шар оркестрації та моделі spaCy і flair для оптимального виявлення в різних мовах і для різних типів сутностей."
  },
  "technicalDetails": {
    "title": "Технічні можливості",
    "subtitle": "Налаштовуване виявлення для європейських ділових документів.",
    "items": [
      {
        "title": "Підтримка мов",
        "description": "Попередньо навчені NLP-моделі для німецької, англійської, французької, італійської та іспанської мов. Розпізнавання сутностей з урахуванням особливостей кожної мови для точного виявлення PII в європейському діловому контексті.",
        "tone": "primary"
      },
      {
        "title": "Налаштовувані сутності PII",
        "description": "Обирайте, які типи персональних даних слід виявляти й анонімізувати: імена осіб, адреси електронної пошти, номери телефонів, поштові адреси, банківські рахунки IBAN, номери кредитних карток, номери соціального страхування, податкові номери, номери паспортів тощо.",
        "tone": "info"
      },
      {
        "title": "Режими анонімізації",
        "description": "Режим заміни підставляє замість PII універсальні заповнювачі для документації, пов’язаної з дотриманням нормативних вимог. Режим фіктивних даних використовує правдоподібні підставні значення для тестових середовищ і зберігає структуру документа.",
        "tone": "warning"
      },
      {
        "title": "Попередня та подальша обробка",
        "description": "Реалізуйте власну логіку до та після анонімізації. Додавайте перевірку документів, конвертацію форматів, контроль якості, журналювання для аудиту або інтеграцію з подальшими системами через надані інтерфейси.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat доповнює Kyveil вебчатом із ШІ",
    "subtitle": "Новий застосунок використовує той самий підхід до анонімізації перед власне чатом із зовнішніми або внутрішніми системами ШІ.",
    "eyebrow": "Новий застосунок",
    "name": "AnonChat",
    "description": "AnonChat — це вебчат із ШІ, у якому документи Word та Excel можна анонімізувати перед використанням із ChatGPT, Claude, Gemini або внутрішніми LLM. Попередній перегляд показує анонімізований текст, перш ніж вміст потрапить у чат.",
    "statement": "Рішення залишається за користувачем: перевірити, схвалити й лише тоді спілкуватися в чаті.",
    "steps": [
      {
        "title": "Завантажити документ",
        "description": "Файли Word або Excel готуються до анонімізації, перш ніж потрапити в робочий процес із ШІ.",
        "tone": "primary"
      },
      {
        "title": "Анонімізувати PII",
        "description": "Kyveil виявляє персональні дані й замінює або маскує їх за обраними правилами.",
        "tone": "info"
      },
      {
        "title": "Перевірити попередній перегляд",
        "description": "Анонімізований текст видно ще до того, як вміст буде передано в чат.",
        "tone": "warning"
      },
      {
        "title": "Скористатися ШІ-чатом",
        "description": "Лише після перевірки анонімізований вміст використовується в чаті із зовнішніми або внутрішніми LLM.",
        "tone": "success"
      }
    ]
  },
  "deployment": {
    "title": "Варіанти розгортання",
    "subtitle": "Оберіть рішення, яке відповідає вашому сценарію використання.",
    "options": [
      {
        "title": "Настільний застосунок",
        "subtitle": "Автономний застосунок для окремих робочих місць",
        "description": "Настільна версія працює повністю самостійно у Windows, macOS або Linux. Ідеально підходить для користувачів, яким потрібно анонімізувати документи безпосередньо на робочому місці без залежності від мережі.",
        "features": [
          "100 % офлайн — підключення до інтернету не потрібне",
          "Файли ніколи не залишають локальної робочої станції",
          "Пакетна обробка кількох файлів",
          "Графічний інтерфейс користувача для простої роботи",
          "Працює на стандартних офісних комп’ютерах без GPU",
          "Одноразова ліцензія — без підписки"
        ]
      },
      {
        "title": "REST API",
        "subtitle": "Серверне розгортання для інтеграції та масштабування",
        "description": "Версія з API розгортається на ваших власних серверах або в інфраструктурі приватної хмари. Ідеально підходить для інтеграції анонімізації в наявні системи та робочі процеси з великими обсягами обробки.",
        "features": [
          "Розгортання у власній інфраструктурі — локально або в приватній хмарі",
          "Горизонтальне масштабування для високої пропускної здатності",
          "RESTful API з вичерпною документацією",
          "Той самий рушій анонімізації, що й у настільній версії",
          "Приклади інтеграції для поширених платформ",
          "Підтримка налаштування попередньої та подальшої обробки"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "Вебчат із ШІ з попередньою анонімізацією",
        "description": "AnonChat ставить Kyveil перед власне ШІ-чатом. Документи Word та Excel анонімізуються, попередній перегляд перевіряється, і лише після цього вміст використовується з ChatGPT, Claude, Gemini або внутрішніми LLM.",
        "features": [
          "Анонімізація перед чатом замість контролю постфактум",
          "Попередній перегляд анонімізованого тексту перед передаванням",
          "Призначено для запитань до систем ШІ на основі документів",
          "Зменшує ризик надсилання персональних даних зовнішнім моделям",
          "Можна підключити до внутрішніх LLM або корпоративних чат-рішень",
          "Використовує той самий підхід до анонімізації, що й настільний застосунок і REST API"
        ]
      }
    ],
    "bothNote": "Настільний застосунок, REST API та AnonChat розраховані на те, щоб анонімізувати чутливий вміст перед подальшою обробкою."
  },
  "useCases": {
    "title": "Сценарії використання",
    "subtitle": "Від відповідності GDPR до безпечних даних для розробки.",
    "items": [
      {
        "title": "Відповідність GDPR",
        "description": "Анонімізуйте персональні дані перед передаванням документів назовні або внутрішнім підрозділам. Виконуйте вимоги щодо захисту даних для тестових даних, аналітичних наборів даних і документації.",
        "tone": "primary"
      },
      {
        "title": "Підготовка тестових даних",
        "description": "Готуйте реалістичні набори тестових даних так, щоб розробники могли працювати з даними, наближеними до продуктивних, не розкриваючи справжньої особистої інформації.",
        "tone": "info"
      },
      {
        "title": "Обробка кадрових документів",
        "description": "Анонімізуйте заяви кандидатів, особові справи та кадрову документацію для неупередженого розгляду, аудитів відповідності або аналізу даних зі збереженням приватності.",
        "tone": "warning"
      },
      {
        "title": "Аналіз даних",
        "description": "Уможливте аналіз документів, що містять PII, попередньо анонімізувавши чутливу інформацію. Обмінюйтеся наборами даних між командами без ризиків для захисту даних.",
        "tone": "success"
      },
      {
        "title": "Архівування документів",
        "description": "Готуйте документи до довгострокового архівування, видаляючи або анонімізуючи PII відповідно до правил зберігання та норм захисту даних.",
        "tone": "secondary"
      },
      {
        "title": "Безпечне передавання",
        "description": "Передавайте документи зовнішнім партнерам, підрядникам або іншим підрозділам після автоматичного видалення чутливої особистої інформації.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Варіанти та підтримка",
    "subtitle": "Оновлення, розширення та підтримка для продуктивного використання.",
    "items": [
      {
        "title": "Настільний застосунок, API та AnonChat",
        "description": "Kyveil доступний як настільний застосунок і як REST API для інтеграцій. AnonChat — це вебчат із попередньою анонімізацією документів.",
        "tone": "info"
      },
      {
        "title": "Підтримка",
        "description": "Для використання в компаніях ми надаємо технічну підтримку, допомогу з впровадженням та індивідуальне налаштування.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Почніть роботу з Kyveil",
    "subtitle": "Захистіть чутливі документи, перш ніж вони потраплять до ШІ, тестів, аналітики або будуть передані далі.",
    "desktop": "Запит на настільний застосунок",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Контакт щодо розгортання REST API",
    "contact": "Щоб отримати демонстрацію або комерційну пропозицію, напишіть нам.",
    "email": "info@paioneer.eu",
    "apiSubject": "Розгортання Kyveil REST API",
    "contactSubject": "Kyveil: демонстрація або комерційна пропозиція"
  }
};
